import React, { useContext, useRef, useState } from 'react';
import {
  FlatList,
  Platform,
  StyleSheet,
  TextInput,
  ToastAndroid,
  TouchableHighlight,
  View,
} from 'react-native';
import DyteText from './DyteText';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { SvgXml } from 'react-native-svg';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ({
  iconPack = defaultIconPack,
  t,
  onDyteCreatePoll,
}: {
  iconPack?: IconPack;
  t?: DyteI18n;
  onDyteCreatePoll?: any;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  const [createPoll, enableCreatePoll] = useState(false);
  const [pollNameFocus, setNameFocus] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [allOptionsProvided, setAllOptionsProvided] = useState(true);
  const [hide, setHide] = useState(false);
  const pollQuestionRef = useRef('');
  const pollOptionsRef = useRef([
    {
      option: '',
    },
    {
      option: '',
    },
  ]);
  const [pollOptions, setPollOptions] = useState([
    {
      option: '',
    },
    {
      option: '',
    },
  ]);
  const checkAllOptionsProvided = () => {
    setAllOptionsProvided(
      pollOptions.map((value) => value.option.length === 0).indexOf(true) === -1
    );
    return (
      pollOptions.map((value) => value.option.length === 0).indexOf(true) === -1
    );
  };
  const styles = StyleSheet.create({
    createPollContainer: {
      width: '80%',
      flexDirection: 'column',
      backgroundColor: colors.background[800],
      padding: 12,
      margin: 10,
      marginBottom: 100,
      borderRadius: 20,
      alignItems: 'center',
    },
    joinButton: {
      width: '95%',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
      marginVertical: 10,
    },
    pollQueInput: {
      backgroundColor: colors.background[900],
      borderWidth: pollNameFocus ? 1 : 0.5,
      borderRadius: 5,
      borderColor: pollNameFocus ? colors.brand[500] : colors.background[600],
      marginVertical: 10,
      width: '95%',
      height: 100,
      paddingLeft: 10,
      color: colors.text,
    },
    optionInput: {
      backgroundColor: colors.background[900],
      borderWidth: 0.3,
      borderRadius: 5,
      borderColor: allOptionsProvided ? colors.background[600] : colors.danger,
      marginVertical: 10,
      height: 40,
      paddingLeft: 20,
      width: '95%',
      color: colors.text,
    },
    addOption: {
      width: '100%',
      padding: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 2,
    },
    createPoll: {
      width: '100%',
      padding: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 2,
      backgroundColor: colors.brand[500],
    },
    text: {
      color: colors.text,
      textAlign: 'left',
      marginLeft: 10,
    },
    iconStyle: {
      height: 20,
      width: 20,
      marginRight: 8,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background[1000],
      borderRadius: 8,
    },
  });

  const renderCreatePoll = () => {
    return (
      <View style={styles.createPollContainer}>
        <DyteText size="sm">{t('polls.question')}</DyteText>
        <TextInput
          onPressIn={() => setNameFocus(true)}
          onEndEditing={() => setNameFocus(false)}
          style={styles.pollQueInput}
          onChangeText={(text) => {
            pollQuestionRef.current = text;
          }}
          textAlignVertical={'top'}
          multiline={true}
          placeholder={t('polls.question.placeholder')}
          placeholderTextColor={colors.text}
        />
        <FlatList
          data={pollOptions}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{ width: '95%' }}
          renderItem={({ item, index }) => {
            return (
              <TextInput
                style={styles.optionInput}
                placeholder={t('polls.option.placeholder')}
                placeholderTextColor={colors.text}
                value={pollOptions[item.option]}
                onChangeText={(text) => {
                  setAllOptionsProvided(true);
                  let options = pollOptions;
                  options[index].option = text;
                  pollOptionsRef.current = options;
                }}
              />
            );
          }}
        />
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ marginLeft: '5%', width: '100%' }}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            style={{
              ...styles.joinButton,
              backgroundColor: colors.brand[500],
            }}
            onPress={() => {
              setPollOptions([...pollOptionsRef.current, { option: '' }]);
            }}
          >
            <View style={styles.addOption}>
              {/* eslint-disable-next-line react-native/no-inline-styles */}
              <DyteText style={{ marginLeft: 10, color: colors.textOnBrand }}>
                {t('polls.option')}
              </DyteText>
            </View>
          </TouchableHighlight>
        </View>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ width: '100%' }}>
          <View
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: 10,
              marginBottom: 10,
            }}
          >
            <TouchableHighlight
              underlayColor={colors.background[800]}
              onPress={() => setAnonymous(!anonymous)}
              style={styles.iconStyle}
            >
              {anonymous ? (
                <SvgXml
                  xml={iconPack.checkmark.replace(
                    'currentColor',
                    colors.success
                  )}
                  width={16}
                  height={16}
                />
              ) : (
                <View />
              )}
            </TouchableHighlight>
            <DyteText>{t('polls.results.anon')}</DyteText>
          </View>
          <View
            // eslint-disable-next-line react-native/no-inline-styles
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              paddingLeft: 10,
            }}
          >
            <TouchableHighlight
              underlayColor={colors.background[800]}
              onPress={() => setHide(!hide)}
              style={styles.iconStyle}
            >
              {hide ? (
                <SvgXml
                  xml={iconPack.checkmark.replace(
                    'currentColor',
                    colors.success
                  )}
                  width={16}
                  height={16}
                />
              ) : (
                <View />
              )}
            </TouchableHighlight>
            <DyteText>{t('polls.results.hide')}</DyteText>
          </View>
        </View>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ marginLeft: '5%', width: '100%' }}>
          <TouchableHighlight
            underlayColor={colors.background[800]}
            style={[
              styles.joinButton,
              {
                backgroundColor: !createPoll
                  ? colors.brand[500]
                  : colors.background[800],
              },
            ]}
            onPress={() => {
              if (
                pollQuestionRef.current.length !== 0 &&
                checkAllOptionsProvided()
              ) {
                onDyteCreatePoll(
                  pollQuestionRef.current,
                  pollOptions.map((item) => item.option),
                  anonymous,
                  hide
                );
                pollQuestionRef.current = '';
                pollOptionsRef.current = [
                  {
                    option: '',
                  },
                  {
                    option: '',
                  },
                ];
                setAnonymous(false);
                setHide(false);
              } else {
                if (Platform.OS === 'android')
                  ToastAndroid.show('Invalid Inputs !', ToastAndroid.SHORT);
              }
              enableCreatePoll(!createPoll);
            }}
          >
            <View style={styles.createPoll}>
              <DyteText
                // eslint-disable-next-line react-native/no-inline-styles
                style={{
                  marginLeft: 10,
                  fontWeight: '600',
                }}
                onBrand
              >
                {t('polls.create')}
              </DyteText>
            </View>
          </TouchableHighlight>
        </View>
      </View>
    );
  };
  return renderCreatePoll();
}
